import React, { useEffect, useRef, useState } from 'react';
import useVisibility from 'react-use-visibility';
import Button from '../Button';
import Divider from '../Divider';
import HeroPanel from '../HeroPanel';
import Layout from '../Layout';

import swapGenderImage from '../../images/swap-gender.svg';

import * as styles from './ResultScreen.module.scss';
import { Gender, Hero } from '../../configs';
import { clsx } from '../../utils/utils';

export interface ResultScreenProps {
  onHeroClick?: () => void;
  heroSlug: Hero;
  heroGender: Gender;
  heroName: string;
  maleHeroImage: any;
  femaleHeroImage: any;
  heroLogoImage: any;
  superPower: string;
  weakness: string;
  description: string;
  onSwapGenderClick: () => void;
  onShareClick: () => void;
  onRetryClick: () => void;
}

const ResultScreen = ({
  onHeroClick,
  heroSlug,
  heroGender,
  heroName,
  maleHeroImage,
  femaleHeroImage,
  heroLogoImage,
  superPower,
  weakness,
  description,
  onSwapGenderClick,
  onShareClick,
  onRetryClick,
}: ResultScreenProps): React.ReactElement => {
  const [readyToShowFloatingShareBtn, setReadyToShowFloatingShareBtn] = useState<boolean>(false);
  const staticShareBtnRef = useRef<HTMLDivElement | null>(null);

  const isStaticShareBtnVisible = useVisibility(staticShareBtnRef.current, { partial: true });

  useEffect(() => {
    setTimeout(() => {
      setReadyToShowFloatingShareBtn(true);
    }, 1000);
  }, []);

  return (
    <Layout noMobileShare>
      <div className={clsx(styles.container, 'hero-' + heroSlug, 'heroGender-' + heroGender)}>
        <div className={styles.contentContainer}>
          <div className={styles.heroPanelContainer}>
            <HeroPanel
              className={styles.heroPanel}
              currentGender={heroGender}
              maleHeroImage={maleHeroImage}
              femaleHeroImage={femaleHeroImage}
              onClick={onHeroClick}
            >
              <Button
                className={styles.swapGenderButton}
                small
                onClick={e => {
                  e.stopPropagation();
                  onSwapGenderClick();
                }}
                title="Swap gender"
              >
                <img className={styles.swapGenderIcon} src={swapGenderImage}></img>
              </Button>
            </HeroPanel>
          </div>
          <div className={styles.heroDescriptionContainer}>
            <img className={styles.heroLogo} src={heroLogoImage} alt={heroName}></img>
            <p className={styles.superPowerLabel}>Super Power</p>
            <p className={styles.superPower}>{superPower}</p>
            <p className={styles.weaknessLabel}>Weakness</p>
            <p className={styles.weakness}>{weakness}</p>
            <Divider className={styles.divider}></Divider>
            <div className={styles.description}>
              {description.split('\n\n').map((descriptionPart, i) => (
                <p key={i}>{descriptionPart}</p>
              ))}
            </div>
            <div className={styles.buttonsContainer} ref={staticShareBtnRef}>
              <Button className={styles.shareButton} onClick={onShareClick}>
                Share
              </Button>
              <Button className={styles.retryButton} secondary onClick={onRetryClick}>
                Retry
              </Button>
            </div>
          </div>
        </div>
        <Button
          small
          className={clsx(
            styles.floatingShareButton,
            readyToShowFloatingShareBtn &&
              !isStaticShareBtnVisible &&
              styles.floatingShareButtonVisible,
          )}
          onClick={onShareClick}
        >
          Share
        </Button>
      </div>
    </Layout>
  );
};

export default ResultScreen;
